import React, { useState, useRef } from 'react';
import { Check, CheckCheck, Play, Pause, Download, FileText } from 'lucide-react';
import clsx from 'clsx';
import type { Message } from '../../stores/whatsapp/chatStore';
import { formatTime } from '../../services/whatsapp/waFormatters';
import { WHATSAPP_BASE_URL } from '../../services/whatsapp/waApi';

/** Resolve media URLs: relative paths get the ContaChat backend base URL prepended */
function resolveMediaUrl(url: string | undefined): string | undefined {
  if (!url) return url;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `${WHATSAPP_BASE_URL.replace(/\/$/, '')}/${url.replace(/^\//, '')}`;
}

/** Image lightbox modal */
function ImageModal({ src, onClose }: { src: string; onClose: () => void }) {
  // Close on Escape key (listen on document since modal div may not have focus)
  React.useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80"
      onClick={onClose}
    >
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/click-events-have-key-events */}
      <img
        src={src}
        alt="Imagem ampliada"
        className="max-h-[90vh] max-w-[90vw] rounded-lg object-contain shadow-2xl"
        onClick={e => e.stopPropagation()}
      />
      <button
        type="button"
        onClick={onClose}
        className="absolute right-4 top-4 rounded-full bg-black/50 p-2 text-white hover:bg-black/70"
      >
        ✕
      </button>
    </div>
  );
}

interface MessageBubbleProps {
  message: Message;
  isOwn: boolean;
  /** Se true, conteudo e borrado (departamento sem permissao) */
  redacted?: boolean;
}

/** Renderiza texto com formatacao WhatsApp: *negrito*, _italico_, ~tachado~ */
function WaFormattedText({ text }: { text: string }) {
  const parts = text.split(/(\*[^*]+\*|_[^_]+_|~[^~]+~)/g);
  return (
    <>
      {parts.map(part => {
        if (part.startsWith('*') && part.endsWith('*')) {
          return <strong key={part}>{part.slice(1, -1)}</strong>;
        }
        if (part.startsWith('_') && part.endsWith('_')) {
          return <em key={part}>{part.slice(1, -1)}</em>;
        }
        if (part.startsWith('~') && part.endsWith('~')) {
          return <s key={part}>{part.slice(1, -1)}</s>;
        }
        return part;
      })}
    </>
  );
}

// Audio player inline
function AudioPlayer({ src, isOwn }: { src: string; isOwn: boolean }) {
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef<HTMLAudioElement>(null);

  const toggle = () => {
    if (!audioRef.current) return;
    if (playing) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setPlaying(!playing);
  };

  const formatDuration = (s: number) => {
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  return (
    <div className="flex min-w-[200px] items-center gap-3">
      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <audio
        ref={audioRef}
        src={src}
        onTimeUpdate={() => {
          if (audioRef.current)
            setProgress((audioRef.current.currentTime / (audioRef.current.duration || 1)) * 100);
        }}
        onLoadedMetadata={() => {
          if (audioRef.current) setDuration(audioRef.current.duration);
        }}
        onEnded={() => {
          setPlaying(false);
          setProgress(0);
        }}
      />
      <button
        type="button"
        onClick={toggle}
        className={clsx(
          'flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full transition-colors',
          isOwn ? 'bg-green-700 hover:bg-green-800' : 'bg-gray-200 hover:bg-gray-300',
        )}
      >
        {playing ? (
          <Pause size={14} className={isOwn ? 'text-white' : 'text-gray-700'} />
        ) : (
          <Play size={14} className={clsx(isOwn ? 'text-white' : 'text-gray-700', 'ml-0.5')} />
        )}
      </button>
      <div className="min-w-0 flex-1">
        <div
          className={clsx(
            'h-1.5 overflow-hidden rounded-full',
            isOwn ? 'bg-green-700/50' : 'bg-gray-200',
          )}
        >
          <div
            className={clsx(
              'h-full rounded-full transition-all',
              isOwn ? 'bg-white' : 'bg-green-500',
            )}
            style={{ width: `${progress}%` }}
          />
        </div>
        <span
          className={clsx('mt-0.5 block text-[10px]', isOwn ? 'text-green-200' : 'text-gray-400')}
        >
          {duration > 0 ? formatDuration(duration) : '0:00'}
        </span>
      </div>
    </div>
  );
}

// Document attachment
function DocumentAttachment({
  filename,
  size,
  url,
  isOwn,
}: {
  filename: string;
  size?: number;
  url?: string;
  isOwn: boolean;
}) {
  const ext = filename.split('.').pop()?.toUpperCase() || 'DOC';
  const sizeStr = size ? `${(size / 1024).toFixed(0)} KB` : '';

  return (
    <div
      className={clsx(
        'flex min-w-[200px] items-center gap-3 rounded-xl px-3 py-2',
        isOwn ? 'bg-green-700/30' : 'bg-gray-100',
      )}
    >
      <div
        className={clsx(
          'flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg',
          isOwn ? 'bg-green-700/50' : 'bg-white',
        )}
      >
        <FileText size={18} className={isOwn ? 'text-white' : 'text-gray-500'} />
      </div>
      <div className="min-w-0 flex-1">
        <p className={clsx('truncate text-sm font-medium', isOwn ? 'text-white' : 'text-gray-800')}>
          {filename}
        </p>
        <p className={clsx('text-[10px]', isOwn ? 'text-green-200' : 'text-gray-400')}>
          {ext} {sizeStr && `· ${sizeStr}`}
        </p>
      </div>
      {url && (
        <a
          href={url}
          target="_blank"
          rel="noreferrer"
          className={clsx(
            'flex-shrink-0 rounded-full p-1.5',
            isOwn ? 'hover:bg-green-700' : 'hover:bg-gray-200',
          )}
        >
          <Download size={16} className={isOwn ? 'text-white' : 'text-gray-500'} />
        </a>
      )}
    </div>
  );
}

export default function MessageBubble({ message, isOwn, redacted }: MessageBubbleProps) {
  const [showImageModal, setShowImageModal] = useState(false);
  // Mensagem borrada (departamento sem permissao) — efeito liquid glass
  if (redacted && message.sender_type !== 'system') {
    return (
      <div className={clsx('flex py-0.5', isOwn ? 'justify-end' : 'justify-start')}>
        <div
          className={clsx(
            'relative max-w-[85%] select-none overflow-hidden rounded-2xl px-4 py-3 sm:max-w-md',
            isOwn ? 'rounded-br-md' : 'rounded-bl-md',
          )}
          style={{
            background: 'rgba(255, 255, 255, 0.15)',
            backdropFilter: 'blur(12px) saturate(1.2)',
            WebkitBackdropFilter: 'blur(12px) saturate(1.2)',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            boxShadow: '0 2px 16px rgba(0, 0, 0, 0.06), inset 0 1px 0 rgba(255, 255, 255, 0.4)',
          }}
        >
          <div className="pointer-events-none" style={{ filter: 'blur(6px)' }}>
            <p className="text-sm leading-relaxed text-gray-400">
              {message.content
                ? message.content.slice(0, 40).replace(/./g, '█')
                : '████████████████'}
            </p>
            {message.media_url && <div className="mt-1 h-8 w-24 rounded bg-gray-200/50" />}
          </div>
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="rounded-full bg-white/60 px-2.5 py-1 text-[10px] font-medium text-gray-400/80 backdrop-blur-sm">
              Outro departamento
            </span>
          </div>
          <p
            className={clsx(
              'relative z-10 mt-1 text-[10px]',
              isOwn ? 'text-right text-gray-400/60' : 'text-gray-300/60',
            )}
          >
            {formatTime(message.created_at)}
          </p>
        </div>
      </div>
    );
  }

  // Mensagem do sistema
  if (message.sender_type === 'system') {
    const isStart = message.content.includes('Atendimento iniciado');
    const isEnd = message.content.includes('Chamado encerrado');
    const isTransfer = message.content.includes('Transferido');
    const isResumo = message.content.includes('Resumo IA');

    // Marcador de inicio/fim de atendimento
    if (isStart || isEnd) {
      return (
        <div className="flex items-center gap-3 px-2 py-3">
          <div
            className="h-px flex-1"
            style={{ backgroundColor: isStart ? '#34C759' : '#8E8E93' }}
          />
          <div
            className={clsx(
              'flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-semibold',
              isStart
                ? 'border border-green-200 bg-green-50 text-green-700'
                : 'border border-gray-200 bg-gray-100 text-gray-600',
            )}
          >
            <span>{isStart ? '▶' : '■'}</span>
            <span>{message.content}</span>
          </div>
          <div
            className="h-px flex-1"
            style={{ backgroundColor: isStart ? '#34C759' : '#8E8E93' }}
          />
        </div>
      );
    }

    // Resumo IA
    if (isResumo) {
      return (
        <div className="flex justify-center py-2">
          <div className="max-w-md rounded-xl border border-purple-100 bg-purple-50 px-4 py-2.5 text-xs text-purple-700">
            <div className="mb-1 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-purple-500">
              <span>🤖</span> Resumo IA
            </div>
            <p className="whitespace-pre-wrap leading-relaxed">
              {message.content.replace('📋 Resumo IA:\n', '')}
            </p>
          </div>
        </div>
      );
    }

    // Transferencia
    if (isTransfer) {
      return (
        <div className="flex items-center gap-3 px-2 py-2">
          <div className="h-px flex-1 bg-blue-200" />
          <div className="rounded-full border border-blue-200 bg-blue-50 px-3 py-1.5 text-[11px] font-medium text-blue-700">
            ↗ {message.content}
          </div>
          <div className="h-px flex-1 bg-blue-200" />
        </div>
      );
    }

    // Sistema generico
    return (
      <div className="flex justify-center py-2">
        <div className="max-w-sm rounded-full bg-gray-200 px-4 py-1.5 text-center text-xs italic text-gray-600">
          {message.content}
        </div>
      </div>
    );
  }

  // Mensagem automática (bot) enviada ao cliente
  if (message.sender_type === 'bot') {
    return (
      <div className="flex justify-end py-0.5">
        <div className="max-w-[85%] rounded-2xl rounded-br-md border-l-4 border-green-700 bg-green-500 px-4 py-2.5 text-white shadow-sm sm:max-w-md">
          <div className="mb-1 flex items-center gap-1.5">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-green-200">
              Automática
            </span>
          </div>
          <p className="whitespace-pre-wrap break-words text-sm leading-relaxed">
            {message.content}
          </p>
          <div className="mt-1 flex items-center justify-end gap-1">
            <span className="text-[10px] text-green-200">{formatTime(message.created_at)}</span>
            <Check className="h-3.5 w-3.5 text-green-300" />
          </div>
        </div>
      </div>
    );
  }

  // Nota interna
  if (message.is_internal_note) {
    return (
      <div className={clsx('flex py-1', isOwn ? 'justify-end' : 'justify-start')}>
        <div className="max-w-md rounded-2xl border border-amber-200 bg-amber-50 px-4 py-2.5 shadow-sm">
          <div className="mb-1 flex items-center gap-1.5">
            <span className="text-xs font-semibold text-amber-600">Nota interna</span>
          </div>
          <p className="whitespace-pre-wrap text-sm text-amber-900">{message.content}</p>
          <div className="mt-1.5 flex items-center justify-end gap-1">
            <span className="text-[10px] text-amber-400">{formatTime(message.created_at)}</span>
          </div>
        </div>
      </div>
    );
  }

  const isAudio = message.message_type === 'audio';
  const isDocument = message.message_type === 'document';
  const isImage = message.message_type === 'image';

  // Mensagem normal
  return (
    <div className={clsx('flex py-0.5', isOwn ? 'justify-end' : 'justify-start')}>
      <div
        className={clsx(
          'max-w-[85%] px-4 py-2.5 shadow-sm sm:max-w-md',
          isOwn
            ? 'rounded-2xl rounded-br-md bg-green-600 text-white'
            : 'rounded-2xl rounded-bl-md border border-gray-100 bg-white text-gray-900',
        )}
      >
        {/* Áudio */}
        {isAudio && message.media_url && (
          <AudioPlayer src={resolveMediaUrl(message.media_url)!} isOwn={isOwn} />
        )}

        {/* Documento */}
        {isDocument && (
          <DocumentAttachment
            filename={message.media_filename || 'documento'}
            size={message.media_size}
            url={resolveMediaUrl(message.media_url)}
            isOwn={isOwn}
          />
        )}

        {/* Imagem */}
        {isImage && message.media_url && (
          <div className="mb-2">
            {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/click-events-have-key-events */}
            <img
              src={resolveMediaUrl(message.media_url)}
              alt="Imagem"
              className="max-h-64 max-w-full cursor-pointer rounded-xl object-cover"
              onClick={() => setShowImageModal(true)}
            />
            {showImageModal && (
              <ImageModal
                src={resolveMediaUrl(message.media_url)!}
                onClose={() => setShowImageModal(false)}
              />
            )}
          </div>
        )}

        {/* Texto */}
        {message.content && (
          <p className="whitespace-pre-wrap break-words text-sm leading-relaxed">
            <WaFormattedText text={message.content} />
          </p>
        )}

        <div className="mt-1 flex items-center justify-end gap-1">
          <span className={clsx('text-[10px]', isOwn ? 'text-green-200' : 'text-gray-400')}>
            {formatTime(message.created_at)}
          </span>
          {isOwn && (
            <>
              {message.status === 'sent' && <Check className="h-3.5 w-3.5 text-green-300" />}
              {message.status === 'delivered' && (
                <CheckCheck className="h-3.5 w-3.5 text-green-300" />
              )}
              {message.status === 'read' && <CheckCheck className="h-3.5 w-3.5 text-white" />}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
